import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import type { QuoteStep, ScheduleOverrides } from '../types/quote.types';
import type { Dealer } from '@features/dealers/types/dealers.types';
import type { Vehicle } from '@features/vehiclesearch/types/vinvrmsearch.types';
import type { VehicleDerivative } from '@features/vehiclesearch/types/manualsearch.types';
import type { Product } from '@features/products/types/products.types';
import type { Customer } from '@features/customers/types/customers.types';
import type { PaymentScheduleOptions } from '@features/service-plans/types/payment.types';
import type { SmriqConfirmedVehicle, SmriqPlanSettings } from '@features/service-plans/types/smriq.types';
import { isValidPaymentSelection, type PaymentSelection } from '../types/schedule.types';
import { minimumConfiguredServices } from '@features/service-plans/utils/minimumConfiguredServices';
import { smriqSessionVehicleKey } from '@features/service-plans/utils/smriqSessionVehicleKey';
import { isSmriqProduct } from '@features/service-plans/utils/isSmriqProduct';
import { dealerCollectedAxis } from '../utils/collectionRouting';
import { hasDatedLines } from '../utils/basketDates';
import { AUTH_LOGOUT_ACTION_TYPE } from '@features/auth/store/actionTypes';
import { initialState } from './quoteInitialState';
import { clearSteps } from './clearSteps';
import { basketReducers } from './basketReducers';

interface SavedQuoteIdentity {
    draftId: string;
    quoteId: string;
    quoteRef: string;
    status: 'live' | 'dead' | 'completed';
    version: number | undefined;
}

// A different product invalidates everything derived from it, not just the
// add-on keys: the configuration and the confirmed payment go with it.
const PRODUCT_CHANGE_STEPS: readonly QuoteStep[] = ['product', 'configuration', 'payment'];

const quoteSlice = createSlice({
    name: 'quote',
    initialState,
    reducers: {
        ...basketReducers,
        // Navigation actions
        setCurrentStep: (state, action: PayloadAction<QuoteStep>) => {
            state.currentStep = action.payload;
        },

        completeStep: (state, action: PayloadAction<QuoteStep>) => {
            if (!state.completedSteps.includes(action.payload)) {
                state.completedSteps.push(action.payload);
            }
        },

        setQuoteId: (state, action: PayloadAction<string>) => {
            state.quoteId = action.payload;
        },

        setQuoteVersion: (state, action: PayloadAction<number | undefined>) => {
            state.version = action.payload;
        },

        setSaveDirty: (state, action: PayloadAction<boolean>) => {
            state.saveDirty = action.payload;
        },

        ensureDraftId: {
            reducer: (state, action: PayloadAction<string>) => {
                if (!state.draftId) {
                    state.draftId = action.payload;
                }
            },
            prepare: () => ({ payload: uuidv4() }),
        },

        quoteSaved: (state, action: PayloadAction<SavedQuoteIdentity>) => {
            const { draftId, quoteId, quoteRef, status, version } = action.payload;
            if (state.draftId !== draftId) {
                return;
            }
            state.quoteId = quoteId;
            if (quoteRef) {
                state.quoteRef = quoteRef;
            }
            state.status = status;
            state.version = version;
        },

        setQuoteRef: (state, action: PayloadAction<string>) => {
            state.quoteRef = action.payload;
        },

        setStatus: (state, action: PayloadAction<'live' | 'dead' | 'completed'>) => {
            state.status = action.payload;
        },

        // Data selection actions
        setSelectedDealer: (state, action: PayloadAction<Dealer | null>) => {
            // A dealer receipt records money THAT dealer took, so a changed dealer
            // invalidates the payment step carrying it, on EITHER axis.
            if (
                action.payload?.id !== state.selectedDealer?.id &&
                dealerCollectedAxis(state.paymentSelection?.collectionRouting) !== null
            ) {
                clearSteps(state, ['payment']);
            }
            state.selectedDealer = action.payload;
            if (action.payload) {
                quoteSlice.caseReducers.completeStep(state, {
                    payload: 'dealer',
                    type: 'quote/completeStep',
                });
                state.errors.dealer = [];
            }
        },

        setSelectedVehicle: (state, action: PayloadAction<Vehicle | VehicleDerivative | null>) => {
            // The vendor session prices the vehicle it was confirmed against, so a changed
            // vehicle invalidates it and forces a re-confirm.
            if (
                smriqSessionVehicleKey(action.payload) !==
                smriqSessionVehicleKey(state.selectedVehicle)
            ) {
                state.servicePlanSessionId = null;
                state.smriqConfirmedVehicle = null;
                // An orphaned SMRiQ service crashes the basket price, so the stale schedule
                // goes with its session; the plan step regenerates on the next visit.
                if (isSmriqProduct(state.selectedProduct)) {
                    state.configuredServices = [];
                    state.scheduleOverrides = undefined;
                    state.smriqPlanSettings = null;
                    if (!hasDatedLines(state.configuredServices, state.configuredAddOns)) {
                        state.anchorDate = null;
                    }
                }
            }
            state.selectedVehicle = action.payload;
            if (action.payload) {
                quoteSlice.caseReducers.completeStep(state, {
                    payload: 'vehicle',
                    type: 'quote/completeStep',
                });
                state.errors.vehicle = [];
            }
        },

        setSelectedProduct: (state, action: PayloadAction<Product | null>) => {
            if (action.payload?.id !== state.selectedProduct?.id) {
                clearSteps(state, PRODUCT_CHANGE_STEPS);
            }
            state.selectedProduct = action.payload;
            if (action.payload) {
                quoteSlice.caseReducers.completeStep(state, {
                    payload: 'product',
                    type: 'quote/completeStep',
                });
                state.errors.product = [];
            }
        },

        // Removing a non-mandatory auto line must not have the seeding hook re-add it.
        dismissAutoAddOn: (state, action: PayloadAction<string>) => {
            const dismissed = state.dismissedAutoAddOnIds ?? [];
            if (!dismissed.includes(action.payload)) {
                state.dismissedAutoAddOnIds = [...dismissed, action.payload];
            }
        },

        // Set on quote load: the loaded lines rebuild the list, so the previous
        // journey's dismissals must not carry into this quote.
        requestAutoAddOnBaseline: (state) => {
            state.dismissedAutoAddOnIds = [];
            state.autoAddOnBaselinePending = true;
        },

        // Deduplicated so a repeated baseline (StrictMode, or a reload landing
        // before the flag clears) leaves the list unchanged.
        applyAutoAddOnBaseline: (state, action: PayloadAction<string[]>) => {
            const dismissed = state.dismissedAutoAddOnIds ?? [];
            state.dismissedAutoAddOnIds = [...new Set([...dismissed, ...action.payload])];
            state.autoAddOnBaselinePending = false;
        },

        setResolvedPlanId: (state, action: PayloadAction<string | null>) => {
            state.resolvedPlanId = action.payload;
        },

        setServicePlanSessionId: (state, action: PayloadAction<string | null>) => {
            state.servicePlanSessionId = action.payload;
        },

        setSmriqConfirmedVehicle: (
            state,
            action: PayloadAction<SmriqConfirmedVehicle | null>
        ) => {
            state.smriqConfirmedVehicle = action.payload;
        },

        // The vendor priced THESE mileages, so writing them back is a sync of the live
        // session's own values, never the vehicle change setSelectedVehicle invalidates on.
        syncSmriqConfirmedMileage: (
            state,
            action: PayloadAction<{ mileage: number; annualMileage: number }>
        ) => {
            const vehicle = state.selectedVehicle;
            if (!vehicle || !('current_status' in vehicle)) {
                return;
            }
            vehicle.current_status = {
                ...vehicle.current_status,
                mileage: action.payload.mileage,
                annual_mileage: action.payload.annualMileage,
            };
        },

        setSmriqPlanSettings: (state, action: PayloadAction<SmriqPlanSettings | null>) => {
            state.smriqPlanSettings = action.payload;
        },

        setScheduleOverrides: (state, action: PayloadAction<ScheduleOverrides | undefined>) => {
            state.scheduleOverrides = action.payload;
        },

        setPaymentOptions: (state, action: PayloadAction<PaymentScheduleOptions>) => {
            state.paymentOptions = action.payload;
            // #872. This reducer has no access to QuoteContext's live
            // `selectedProduct` variable — only the slice's own Draft<QuoteData>
            // — so the product's bounds are read from `state.selectedProduct`
            // directly via the same shared reader the step gate and
            // buildQuoteSnapshot() use, rather than a second copy of the
            // fallback logic.
            //
            // When `min === 0` this condition becomes `length >= 0`, which is
            // VACUOUSLY TRUE. That is deliberate, not a deleted guard: a
            // zero-service quote for a min:0 product is a legitimate
            // configuration (D1), so it should still mark `payment` complete on
            // resume, the same as any other satisfied gate.
            if (state.configuredServices.length >= minimumConfiguredServices(state.selectedProduct)) {
                quoteSlice.caseReducers.completeStep(state, {
                    payload: 'payment',
                    type: 'quote/completeStep',
                });
                state.errors.configuration = [];
            }
        },

        // #475-B: persists the salesperson's confirmed scheduling method +
        // schedule snapshot (journey state, D2/D9). Only marks the `payment`
        // step complete when the selection carries a genuine preview
        // snapshot (`isValidPaymentSelection`) — a malformed dispatch must
        // never be able to satisfy the gate.
        setPaymentSelection: (state, action: PayloadAction<PaymentSelection>) => {
            state.paymentSelection = action.payload;
            if (isValidPaymentSelection(action.payload)) {
                quoteSlice.caseReducers.completeStep(state, {
                    payload: 'payment',
                    type: 'quote/completeStep',
                });
                state.errors.payment = [];
            }
        },

        setSelectedCustomer: (state, action: PayloadAction<Customer | null>) => {
            state.selectedCustomer = action.payload;
            if (action.payload) {
                quoteSlice.caseReducers.completeStep(state, {
                    payload: 'customer',
                    type: 'quote/completeStep',
                });
                state.errors.customer = [];
            }
        },

        // Error handling
        setStepErrors: (state, action: PayloadAction<{ step: QuoteStep; errors: string[] }>) => {
            state.errors[action.payload.step] = action.payload.errors;
        },

        clearErrors: (state, action: PayloadAction<QuoteStep>) => {
            state.errors[action.payload] = [];
        },

        // Reset actions
        resetQuote: {
            reducer: (state, action: PayloadAction<string>) => {
                Object.assign(state, initialState, { draftId: action.payload });
            },
            prepare: () => ({ payload: uuidv4() }),
        },

        reloadDraft: {
            reducer: (state, action: PayloadAction<string>) => {
                state.draftId = action.payload;
                state.saveDirty = false;
            },
            prepare: () => ({ payload: uuidv4() }),
        },

        beginServerHydration: (state) => {
            const { draftId, quoteId, currentStep } = state;
            Object.assign(state, initialState, { draftId, quoteId, currentStep });
        },

        // A failed load can never show as this quote, so reset when the slice
        // already holds a different one. Same-id and unsaved cases are left as-is.
        discardStaleQuote: {
            reducer: (state, action: PayloadAction<{ quoteId: string; draftId: string }>) => {
                if (state.quoteId && state.quoteId !== action.payload.quoteId) {
                    Object.assign(state, initialState, { draftId: action.payload.draftId });
                }
            },
            prepare: (quoteId: string) => ({ payload: { quoteId, draftId: uuidv4() } }),
        },

        // The cascade is derived from the RESOLVED order in the payload, not a
        // private literal — the slice cannot read dealer config (see CLEAR_STEP_DATA).
        resetFromStep: (
            state,
            action: PayloadAction<{ step: QuoteStep; order: readonly QuoteStep[] }>
        ) => {
            const { step, order } = action.payload;
            const from = order.indexOf(step);
            // A step outside the resolved order is a caller bug. No-op rather than let
            // slice(-1) cascade from the wrong place and destroy the wrong journey data.
            if (from === -1) {
                console.error('resetFromStep: step not in the resolved order', { step, order });
                return;
            }
            clearSteps(state, order.slice(from));
        },
    },
    extraReducers: (builder) => {
        builder.addCase(AUTH_LOGOUT_ACTION_TYPE, () => initialState);
    },
});

export const {
    setCurrentStep,
    completeStep,
    setQuoteId,
    setQuoteVersion,
    setSaveDirty,
    ensureDraftId,
    quoteSaved,
    setQuoteRef,
    setStatus,
    setSelectedDealer,
    setSelectedVehicle,
    setSelectedProduct,
    setConfiguredServices,
    setConfiguredAddOns,
    setAnchorDate,
    dismissAutoAddOn,
    requestAutoAddOnBaseline,
    applyAutoAddOnBaseline,
    setResolvedPlanId,
    setServicePlanSessionId,
    setSmriqConfirmedVehicle,
    syncSmriqConfirmedMileage,
    setSmriqPlanSettings,
    setScheduleOverrides,
    applyDerivedSchedule,
    setPaymentOptions,
    setPaymentSelection,
    setSelectedCustomer,
    setStepErrors,
    clearErrors,
    resetQuote,
    reloadDraft,
    beginServerHydration,
    discardStaleQuote,
    resetFromStep,
} = quoteSlice.actions;

export default quoteSlice.reducer;
